import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Formik, Form } from 'formik'

import StepIndicator from '../../components/step-indicator'
import GiftAid from '../../components/gift-aid'
import PaymentDetails from '../../components/payment-details'
import SubmitButtons from '../../components/submit-buttons'

import MembershipFormContext from '../../context/membership'
import initialValues from '../../data/initialValues'
import validation from '../../validation/membership'
import { getSS, setSS, cleanObject, loadIndexPage } from '../../helpers'
import { saveMembership } from '../../api'

const Payment: React.FC = (props: any) => {

  const navigate = useNavigate()
  const params = useParams()
  const membershipForm = useContext(MembershipFormContext)
  const membership_form = (window as any).KEW.collections.membership_form[0]
  const [ dataLayerInitialPush3, setDataLayerInitialPush3 ] = useState(getSS('dataLayerInitialPush3'))
  const [ payment, setPayment ] = useState(initialValues.payment)
  const [ submittingPayPal, setSubmittingPayPal ] = useState(false)
  const url = membership_form.private_form ? 'private/' + params.url : params.url
  let membership = getSS('membership')
  const wakehurstMembership = getSS('membership')?.selected_membership?.wakehurst_membership


  useEffect(() => {
    if (!membership || membership.process_payment) {
      loadIndexPage(navigate, url)
    }
    document.title = 'Payment - ' + (window as any).KEW.collections.membership_form[0].meta_title
  }, [membership, navigate, url])

  useEffect(() => {
    membershipForm.preSelectedMembership ? membershipForm.setCurrentStep(2) : membershipForm.setCurrentStep(3)
    if (getSS('payment')) {
      setPayment(getSS('payment'))
    }
  }, [membershipForm])


  const braintreePayPalInit = (membership: any) => {

    membershipForm.showLoading();

    const paypalButton: any = document.querySelector('#m-pay-by-paypal-button');

    // ensure we don't attach multiple handlers
    const attachHandler = (paypalInstance: any) => {
      // remove any old handler
      const old = (paypalButton as any)._paypalHandler;
      if (old) {
        paypalButton.removeEventListener('click', old);
        delete (paypalButton as any)._paypalHandler;
      }

      const handleClick = function () {
        // prevent double-submission from UI clicks
        if ((paypalButton as any)._isTokenizing) return;
        (paypalButton as any)._isTokenizing = true;
        paypalInstance.tokenize({
          flow: 'checkout',
          amount: membership.amount,
          currency: 'GBP'
        }, async function (tokenizeErr: any, payload: any) {
          (paypalButton as any)._isTokenizing = false;

          // Stop if there was an error.
          if (tokenizeErr) {
            // Popup blocked
            if (tokenizeErr.code === 'PAYPAL_POPUP_OPEN_FAILED') {
              alert('It looks like pop-up windows are disabled for this website. These need to be allowed for payment via PayPal - please enable them or try a different payment method.');
            } else if (tokenizeErr.type !== 'CUSTOMER') {
              console.error('Error tokenizing:', tokenizeErr);
            }

            // cleanup so subsequent attempts are clean
            membershipForm.hideLoading();
            setSubmittingPayPal(false);
            // remove the handler so a fresh init will reattach once
            paypalButton.removeEventListener('click', handleClick);
            delete (paypalButton as any)._paypalHandler;
            return;
          }

          // success path
          membership.payment_method_token_or_nonce = payload.nonce;
          membership.process_payment = true;
          if (membership.optins_giftaid) {
            membership.giftaid_amount = (membership.amount * 0.25).toFixed(2);
          }

          const savedMembership: any = await saveMembership(membership);
          console.log(savedMembership.data);
          if (savedMembership?.status === 200) {
            setSS('membership', savedMembership.data);
            membershipForm.hideLoading();
            navigate(`/membership/${url}/thanks`);
          } else {
            setSS('api-error', savedMembership.headers['api-error']);
            membershipForm.hideLoading();
            navigate(`/membership/${url}/failed`);
          }

          // remove listener after successful flow
          paypalButton.removeEventListener('click', handleClick);
          delete (paypalButton as any)._paypalHandler;
          setSubmittingPayPal(false);
        });
      };

      (paypalButton as any)._paypalHandler = handleClick;
      paypalButton.addEventListener('click', handleClick);
    };

    // Create a client.
    (window as any).braintree.client.create({
      authorization: membership.payment_token
    }, function (clientErr: any, clientInstance: any) {

      if (clientErr) {
        console.error('Error creating client:', clientErr);
        membershipForm.hideLoading();
        return;
      }

      // Create a PayPal component.
      (window as any).braintree.paypal.create({
        client: clientInstance
      }, function (paypalErr: any, paypalInstance: any) {

        if (paypalErr) {
          console.error('Error creating PayPal:', paypalErr);
          membershipForm.hideLoading();
          return;
        }

        attachHandler(paypalInstance);

        // optionally simulate a click only once and only if desired:
        if (!submittingPayPal) {
          setSubmittingPayPal(true);
          // prefer letting user click; if you must auto-click, do it once:
          setTimeout(() => {
            paypalButton.click();
          }, 100)
          // return
        }
      });
    });
  };

  function onSubmit(data: any) {
    if (submittingPayPal) return;
    console.log(data);
    setSS('payment', data)
    membership = { ...membership, ...data }
    setSS('membership', membership);

    if (!dataLayerInitialPush3) {
      const formType = membership_form.form_type === 'standard' ? 'new' :
        membership_form.form_type === 'upgrade' ? 'upgrade' :
        membership_form.form_type === 'renewal' ? 'renewal' :
        membership_form.form_type === 'ingarden' ? 'new' :
        membership_form.form_type === 'daypass' ? 'new' : 'new';

      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'add_payment_info',
        ecommerce: {
          payment_type: `${membership.payment_method}`,
          items: [{
            item_name: `Membership - ${membership_form.name} - ${membership.selected_membership.value}`,
            item_id: `${membership_form.campaigncode}`,
            price: membership.amount,
            item_brand: `${membership.selected_membership.wakehurst_membership ? 'Wakehurst' : 'Kew'}`,
            item_category: `Membership - ${membership.selected_membership.value}`,
            item_category2: `${formType}`,
            quantity: 1
          }]
        }
      });
      setSS('dataLayerInitialPush3', true)
      setDataLayerInitialPush3(true)
    }

    if (membership.payment_type === 'Single') {
      if (membership.payment_method === 'CreditCard') {
        navigate(`/membership/${url}/card-details`)
      }
      if (membership.payment_method === 'PayPal') {
        braintreePayPalInit(membership)
      }
    }
    if (membership.payment_type === 'Regular') {
      navigate(`/membership/${url}/regular-billing`)
    }
    if (membership.payment_type === 'CardReader') {
      navigate(`/membership/${url}/card-reader`)
    }
  }

  return (
    <>
      { membership &&
        <>
          <Formik
            initialValues={ payment }
            validationSchema={ validation.payment }
            enableReinitialize={true}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={ onSubmit }>
              {(props: any) => {
                props.errors = cleanObject(props.errors)
                return (
                  <>
                    <Form name="membershipForm">
                      <StepIndicator />
                      <PaymentDetails />
                      { !wakehurstMembership &&
                        <GiftAid {...props} />
                      }
                      <SubmitButtons {...props} />

                    </Form>
                  </>
                )
            }}
          </Formik>
        </>
      }
    </>
  )
}

export default Payment